import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supplierService } from '../../services/supplierService';

const SupplierSelector = ({
  value,
  onChange,
  disabled = false,
  error = null,
  suppliers: externalSuppliers = null,
  placeholder = 'Buscar proveedor por nombre, código o documento...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const portalRef = useRef(null);
  const optionRefs = useRef([]);
  const blurCloseTimerRef = useRef(null);
  const suppressBlurCloseRef = useRef(false);

  useEffect(() => {
    if (externalSuppliers !== null) {
      setSuppliers(externalSuppliers);
    } else {
      loadSuppliers();
    }
  }, [externalSuppliers]);

  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom, left: rect.left, width: rect.width });
  };

  const openDropdown = () => {
    updateDropdownPosition();
    setIsOpen(true);
  };

  const cancelScheduledClose = () => {
    if (blurCloseTimerRef.current) {
      window.clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
  };

  const scheduleCloseOnBlur = () => {
    cancelScheduledClose();
    blurCloseTimerRef.current = window.setTimeout(() => {
      if (!suppressBlurCloseRef.current) {
        setIsOpen(false);
        if (!value) setSearchTerm('');
      }
    }, 150);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      const insideControl = dropdownRef.current?.contains(event.target);
      const insidePortal = portalRef.current?.contains(event.target);
      if (!insideControl && !insidePortal) {
        setIsOpen(false);
        if (!value) setSearchTerm('');
      }
    };
    if (isOpen) document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [isOpen, value]);

  useEffect(() => () => cancelScheduledClose(), []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = useMemo(() => {
    if (!searchTerm.trim()) return suppliers;
    const search = searchTerm.toLowerCase();
    return suppliers.filter(
      (supplier) =>
        supplier.name?.toLowerCase().includes(search) ||
        supplier.code?.toLowerCase().includes(search) ||
        supplier.document_number?.toLowerCase().includes(search)
    );
  }, [suppliers, searchTerm]);

  const selectedSupplier = useMemo(() => {
    if (!value) return null;
    return suppliers.find((s) => s.id === parseInt(value, 10));
  }, [suppliers, value]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredSuppliers.length > 0 ? 0 : -1);
  }, [isOpen, searchTerm, filteredSuppliers.length]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const el = optionRefs.current[highlightedIndex];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex, filteredSuppliers]);

  const handleSelect = (supplier) => {
    onChange(supplier.id);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
    cancelScheduledClose();
  };

  const clearSelectionForSearch = () => {
    if (selectedSupplier) onChange('');
  };

  const handleInputKeyDown = (event) => {
    if (disabled) return;
    const { key } = event;
    const count = filteredSuppliers.length;

    if (key === 'ArrowDown') {
      event.preventDefault();
      clearSelectionForSearch();
      if (!isOpen) {
        openDropdown();
        setHighlightedIndex(count > 0 ? 0 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      clearSelectionForSearch();
      if (!isOpen) {
        openDropdown();
        setHighlightedIndex(count > 0 ? count - 1 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
      return;
    }

    if (key === 'Enter') {
      if (!isOpen || highlightedIndex < 0 || !filteredSuppliers[highlightedIndex]) return;
      event.preventDefault();
      handleSelect(filteredSuppliers[highlightedIndex]);
      return;
    }

    if (key === 'Escape' && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className="relative" ref={dropdownRef} style={{ zIndex: isOpen ? 9999 : 'auto' }}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={
            selectedSupplier
              ? `${selectedSupplier.name} - ${selectedSupplier.document_number || '-'}`
              : searchTerm
          }
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) openDropdown();
            if (selectedSupplier) onChange('');
          }}
          onKeyDown={handleInputKeyDown}
          onFocus={() => {
            cancelScheduledClose();
            openDropdown();
          }}
          onBlur={scheduleCloseOnBlur}
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all pr-10 ${
            error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault();
            cancelScheduledClose();
            suppressBlurCloseRef.current = true;
          }}
          onMouseUp={() => {
            window.setTimeout(() => {
              suppressBlurCloseRef.current = false;
            }, 0);
          }}
          onClick={() => {
            if (disabled) return;
            openDropdown();
            inputRef.current?.focus();
          }}
          disabled={disabled}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Abrir lista de proveedores"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen &&
        !disabled &&
        dropdownPosition.width > 0 &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={portalRef}
            className="bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto"
            style={{
              position: 'fixed',
              top: dropdownPosition.top + 4,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: 200,
              zIndex: 99999,
            }}
            role="listbox"
            onMouseDown={(e) => {
              e.preventDefault();
              suppressBlurCloseRef.current = true;
            }}
            onMouseUp={() => {
              window.setTimeout(() => {
                suppressBlurCloseRef.current = false;
              }, 0);
            }}
          >
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">Cargando proveedores...</div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {searchTerm ? 'No se encontraron proveedores' : 'No hay proveedores disponibles'}
              </div>
            ) : (
              <div className="py-1">
                {filteredSuppliers.map((supplier, index) => {
                  const isHighlighted = highlightedIndex === index;
                  const isSelected = selectedSupplier?.id === supplier.id;
                  return (
                    <button
                      key={supplier.id}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={isHighlighted || isSelected}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(supplier)}
                      className={`w-full text-left px-4 py-2 transition-colors ${
                        isHighlighted
                          ? 'bg-teal-100 ring-1 ring-inset ring-teal-400'
                          : isSelected
                            ? 'bg-teal-50'
                            : 'hover:bg-teal-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{supplier.name}</div>
                      <div className="text-xs text-gray-500">
                        {supplier.document_type === 'dni'
                          ? 'DNI'
                          : supplier.document_type === 'other'
                            ? 'Otro'
                            : 'RUC'}
                        : {supplier.document_number || '-'} | Código: {supplier.code}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>,
          document.body
        )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default SupplierSelector;
